import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area } from 'recharts';
import { getNodeMonitoringHistory, getNodeMonitoringMachines } from '../../services/api.service';
import { SELECT } from '@trinetra/shared';

const POLL_INTERVAL = 30000;
const RANGES = ['1h', '6h', '12h', '24h', '7d'];

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

function fmtBytes(b: number) {
  if (!b || b === 0) return '0 bps';
  const bits = b * 8;
  if (bits < 1000) return bits.toFixed(0) + ' bps';
  if (bits < 1000000) return (bits / 1000).toFixed(1) + ' Kbps';
  if (bits < 1000000000) return (bits / 1000000).toFixed(2) + ' Mbps';
  return (bits / 1000000000).toFixed(2) + ' Gbps';
}

function fmtTime(ts: number) {
  const d = new Date(ts * 1000);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function getSeriesLabel(metric: any, index: number) {
  return metric?.device || metric?.machine_name || `series_${index}`;
}

function seriesToChartData(seriesArr: any[]) {
  if (!seriesArr || seriesArr.length === 0) return [];
  const base = seriesArr[0]?.values || [];
  return base.map(([ts]: [number], i: number) => {
    const point: any = { time: ts };
    seriesArr.forEach((s, si) => {
      const val = s.values?.[i]?.[1];
      point[getSeriesLabel(s.metric, si)] = val ? parseFloat(val) : null;
    });
    return point;
  });
}

function getSeriesKeys(seriesArr: any[]) {
  if (!seriesArr || seriesArr.length === 0) return [];
  return seriesArr.map((s, i) => getSeriesLabel(s.metric, i));
}

function ChartCard({ title, icon, children, className = '' }: { title: string; icon: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-lg p-4 ${className}`} style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--foreground)' }}>
        <span>{icon}</span> {title}
      </h3>
      {children}
    </div>
  );
}

function MetricChart({ data, keys, unit = '%', domain, areaMode = false }: { data: any[]; keys: string[]; unit?: string; domain?: any; areaMode?: boolean }) {
  if (!data || data.length === 0) {
    return <div className="text-xs h-32 flex items-center justify-center" style={{ color: 'var(--muted-foreground)' }}>No data available</div>;
  }

  const tooltipFormatter = (v: any) => unit === '%' ? `${parseFloat(v).toFixed(1)}%` : unit === '°C' ? `${parseFloat(v).toFixed(1)}°C` : fmtBytes(parseFloat(v));
  const yDomain = domain || (unit === '%' ? [0, 100] : unit === '°C' ? [0, 'auto'] : ['auto', 'auto']);
  const yTickFormatter = unit === '%' ? (v: number) => `${v}%` : unit === '°C' ? (v: number) => `${v}°C` : (v: number) => fmtBytes(v);

  const ChartComp: any = areaMode ? AreaChart : LineChart;
  const DataComp: any = areaMode ? Area : Line;

  return (
    <ResponsiveContainer width="100%" height={180}>
      <ChartComp data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.5} />
        <XAxis dataKey="time" tickFormatter={fmtTime} tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} interval="preserveStartEnd" minTickGap={40} />
        <YAxis domain={yDomain} tickFormatter={yTickFormatter} tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} width={50} />
        <Tooltip
          contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '11px' }}
          labelFormatter={fmtTime}
          formatter={tooltipFormatter}
        />
        {keys.length > 1 && <Legend wrapperStyle={{ fontSize: '10px' }} />}
        {keys.map((key, i) => (
          <DataComp
            key={key}
            type="monotone"
            dataKey={key}
            stroke={COLORS[i % COLORS.length]}
            fill={areaMode ? COLORS[i % COLORS.length] : undefined}
            fillOpacity={areaMode ? 0.1 : undefined}
            strokeWidth={1.5}
            dot={false}
            connectNulls
          />
        ))}
      </ChartComp>
    </ResponsiveContainer>
  );
}

export default function HistoryTab({ nodeId }: { nodeId: string }) {
  const [range, setRange] = useState('1h');
  const [machineName, setMachineName] = useState('all');
  const [machines, setMachines] = useState<string[]>([]);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    getNodeMonitoringMachines(nodeId).then(setMachines).catch(() => {});
  }, [nodeId]);

  const fetchData = useCallback(async () => {
    try {
      const d = await getNodeMonitoringHistory(nodeId, range, machineName);
      setData(d);
    } catch (e) {
      console.warn('History fetch error', e);
    } finally {
      setLoading(false);
    }
  }, [range, machineName, nodeId]);

  useEffect(() => {
    setLoading(true);
    fetchData();
    timerRef.current = window.setInterval(fetchData, POLL_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchData]);

  const cpuData = seriesToChartData(data?.cpu);
  const cpuKeys = getSeriesKeys(data?.cpu);
  const memData = seriesToChartData(data?.memory);
  const memKeys = getSeriesKeys(data?.memory);
  const diskData = seriesToChartData(data?.disk);
  const diskKeys = getSeriesKeys(data?.disk);
  const tempData = seriesToChartData(data?.cpu_temp);
  const tempKeys = getSeriesKeys(data?.cpu_temp);
  const netRxData = seriesToChartData(data?.network_recv);
  const netRxKeys = getSeriesKeys(data?.network_recv);
  const netTxData = seriesToChartData(data?.network_send);
  const netTxKeys = getSeriesKeys(data?.network_send);
  const ifaceRxData = seriesToChartData(data?.network_recv_per_iface);
  const ifaceRxKeys = getSeriesKeys(data?.network_recv_per_iface);
  const ifaceTxData = seriesToChartData(data?.network_send_per_iface);
  const ifaceTxKeys = getSeriesKeys(data?.network_send_per_iface);
  const gpuUtilData = seriesToChartData(data?.gpu_util);
  const gpuUtilKeys = getSeriesKeys(data?.gpu_util);
  const gpuTempData = seriesToChartData(data?.gpu_temp);
  const gpuTempKeys = getSeriesKeys(data?.gpu_temp);
  const gpuMemData = seriesToChartData(data?.gpu_mem);
  const gpuMemKeys = getSeriesKeys(data?.gpu_mem);
  const gpuEncData = seriesToChartData(data?.gpu_encoder);
  const gpuEncKeys = getSeriesKeys(data?.gpu_encoder);
  const gpuDecData = seriesToChartData(data?.gpu_decoder);
  const gpuDecKeys = getSeriesKeys(data?.gpu_decoder);

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* SegmentedToggle's look; not the component, which hides text labels below sm. */}
        <div role="radiogroup" aria-label="Time range" className="inline-flex flex-none flex-wrap items-center gap-0.5 rounded-lg border border-border bg-card p-0.5">
          {RANGES.map((r) => ({ key: r, label: r })).map((o) => (
            <button
              key={o.key}
              type="button"
              role="radio"
              aria-checked={range === o.key}
              onClick={() => setRange(o.key)}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                range === o.key
                  ? 'bg-muted text-foreground shadow-sm ring-1 ring-inset ring-border'
                  : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
        {machines.length > 0 && (
          <select
            className={`${SELECT} !w-auto min-w-[160px]`}
            value={machineName}
            onChange={e => setMachineName(e.target.value)}
          >
            <option value="all">All Machines</option>
            {machines.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        )}
        {loading && <span className="text-xs animate-pulse" style={{ color: 'var(--muted-foreground)' }}>Updating...</span>}
      </div>

      {loading && !data && (
        <div className="text-sm py-8 text-center" style={{ color: 'var(--muted-foreground)' }}>Loading history...</div>
      )}

      {data && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <ChartCard title="CPU Usage" icon="🖥">
            <MetricChart data={cpuData} keys={cpuKeys} unit="%" areaMode />
          </ChartCard>

          <ChartCard title="Memory Usage" icon="💾">
            <MetricChart data={memData} keys={memKeys} unit="%" areaMode />
          </ChartCard>

          <ChartCard title="Disk Usage" icon="💿">
            <MetricChart data={diskData} keys={diskKeys} unit="%" />
          </ChartCard>

          <ChartCard title="CPU Temperature" icon="🌡">
            <MetricChart data={tempData} keys={tempKeys} unit="°C" />
          </ChartCard>

          {gpuUtilKeys.length > 0 && (
            <ChartCard title="GPU Utilization" icon="🎮">
              <MetricChart data={gpuUtilData} keys={gpuUtilKeys} unit="%" areaMode />
            </ChartCard>
          )}

          {gpuTempKeys.length > 0 && (
            <ChartCard title="GPU Temperature" icon="🔥">
              <MetricChart data={gpuTempData} keys={gpuTempKeys} unit="°C" />
            </ChartCard>
          )}

          {gpuMemKeys.length > 0 && (
            <ChartCard title="GPU Memory (VRAM)" icon="🧠">
              <MetricChart data={gpuMemData} keys={gpuMemKeys} unit="%" areaMode />
            </ChartCard>
          )}

          {gpuEncKeys.length > 0 && (
            <ChartCard title="GPU Encoder" icon="🎬">
              <MetricChart data={gpuEncData} keys={gpuEncKeys} unit="%" areaMode />
            </ChartCard>
          )}

          {gpuDecKeys.length > 0 && (
            <ChartCard title="GPU Decoder" icon="📽️">
              <MetricChart data={gpuDecData} keys={gpuDecKeys} unit="%" areaMode />
            </ChartCard>
          )}

          <ChartCard title="Network Receive (Total)" icon="⬇️">
            <MetricChart data={netRxData} keys={netRxKeys} unit="bytes" />
          </ChartCard>

          <ChartCard title="Network Transmit (Total)" icon="⬆️">
            <MetricChart data={netTxData} keys={netTxKeys} unit="bytes" />
          </ChartCard>

          {ifaceRxKeys.length > 0 && (
            <ChartCard title="Per-Interface Receive" icon="📥" className="xl:col-span-2">
              <MetricChart data={ifaceRxData} keys={ifaceRxKeys} unit="bytes" />
            </ChartCard>
          )}

          {ifaceTxKeys.length > 0 && (
            <ChartCard title="Per-Interface Transmit" icon="📤" className="xl:col-span-2">
              <MetricChart data={ifaceTxData} keys={ifaceTxKeys} unit="bytes" />
            </ChartCard>
          )}
        </div>
      )}
    </div>
  );
}
